import { useCallback, useMemo } from 'react';
import debounce from 'lodash.debounce';

import { useXFetch } from '~/shared/utils/useXFetch';
import { VolumesAutoCompleteResponse } from '~/shared/types/api';

import type { AutoscaleConditionType } from '../autoscaleRulesMenuItems';
import type { OptionType } from './components';

function parseVolumeTagInput(inputValue: string) {
  const tagPattern = /^\s*(?<key>[^:]+)\s*:\s*(?<val>.*)$/;
  const match = tagPattern.exec(inputValue);
  const key = match?.groups?.key?.trim() || inputValue.trim();
  const val = match?.groups?.val?.trim() || '';
  return { key, val };
}

export function useAutocompleteLoader(conditionType: AutoscaleConditionType) {
  const api = useXFetch();

  const loadOptions = useCallback(
    async (inputValue: string): Promise<OptionType[]> => {
      try {
        let field: string;
        let pattern: string = inputValue;
        let useDefaultQuery = false;

        if (!inputValue.trim()) {
          useDefaultQuery = true;
          switch (conditionType) {
            case 'instance':
              pattern = 'i-';
              break;
            case 'volume':
              pattern = 'Name: ';
              break;
            case 'instance_tag':
              pattern = 'Name: ';
              break;
            case 'cluster':
              pattern = 'eks';
              break;
            case 'node':
              pattern = '';
              break;
            default:
              return [];
          }
        }

        switch (conditionType) {
          case 'instance':
            field = 'instanceId';
            break;

          case 'volume': {
            const textToParse = useDefaultQuery ? pattern : inputValue;
            const { key, val } = parseVolumeTagInput(textToParse);
            if (!key) {
              return [];
            }
            field = `tag:${key}`;
            pattern = val;
            break;
          }

          case 'instance_tag': {
            const textToParse = useDefaultQuery ? pattern : inputValue;
            const { key, val } = parseVolumeTagInput(textToParse);
            if (!key) {
              return [];
            }
            field = `instanceTag:${key}`;
            pattern = val;
            break;
          }

          case 'cluster':
            field = 'clusterName';
            break;

          case 'node':
            field = 'nodeGroupName';
            break;

          default:
            return [];
        }

        const qs = new URLSearchParams({ field, pattern }).toString();
        const response = await api<VolumesAutoCompleteResponse>(
          `/api/v1/volumes/autocomplete?${qs}`,
          { cache: 'no-store' }
        );
        const values = response.values || [];

        // Special handling for volume tags and instance tags to return "key:value" format
        if (conditionType === 'volume' || conditionType === 'instance_tag') {
          const textToParse = useDefaultQuery ? 'Name: ' : inputValue;
          const { key } = parseVolumeTagInput(textToParse);
          return values.map((v) => {
            const fullTag = key && v ? `${key}:${v}` : key || v;
            return { value: fullTag, label: fullTag };
          });
        }

        return values.map((value) => ({ value, label: value }));
      } catch (error) {
        console.error('Error loading autocomplete options:', error);
        return [];
      }
    },
    [conditionType, api]
  );

  const debouncedLoadOptions = useMemo(() => {
    const debouncedFn = debounce((inputValue: string, callback: (result: OptionType[]) => void) => {
      loadOptions(inputValue)
        .then(callback)
        .catch((error) => {
          console.error('Error in debounced load:', error);
          callback([]);
        });
    }, 300);

    return (inputValue: string): Promise<OptionType[]> => {
      return new Promise((resolve) => {
        debouncedFn(inputValue, resolve);
      });
    };
  }, [loadOptions]);

  return debouncedLoadOptions;
}
